export interface WebinarChatTextPart {
    type: 'text' | 'link';
    text: string;
    url?: string;
}

const urlPattern = /https?:\/\/[^\s<>"'`]+/gi;
const trailingPunctuationPattern = /[.,!?;:]$/;
const closingPairs: Record<string, string> = {
    ')': '(',
    ']': '[',
    '}': '{',
};

function appendTextPart(parts: WebinarChatTextPart[], text: string): void {
    if (!text) return;
    const last = parts[parts.length - 1];
    if (last?.type === 'text') {
        last.text += text;
        return;
    }
    parts.push({type: 'text', text});
}

function hasAllowedBoundary(value: string, index: number): boolean {
    if (index === 0) return true;
    return /[\s([{"']/.test(value[index - 1]);
}

function countChar(value: string, char: string): number {
    let count = 0;
    for (const current of value) {
        if (current === char) count += 1;
    }
    return count;
}

function trimUrlSuffix(rawUrl: string): {url: string; suffix: string} {
    let url = rawUrl;
    let suffix = '';

    while (url && trailingPunctuationPattern.test(url)) {
        suffix = url.slice(-1) + suffix;
        url = url.slice(0, -1);
    }

    while (url) {
        const closing = url.slice(-1);
        const opening = closingPairs[closing];
        if (!opening) break;
        if (countChar(url, closing) <= countChar(url, opening)) break;
        suffix = closing + suffix;
        url = url.slice(0, -1);
    }

    return {url, suffix};
}

function isSafeHttpUrl(value: string): boolean {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

export function linkifyWebinarChatText(value: unknown): WebinarChatTextPart[] {
    const text = typeof value === 'string' ? value : String(value ?? '');
    const parts: WebinarChatTextPart[] = [];
    let cursor = 0;
    urlPattern.lastIndex = 0;

    for (let match = urlPattern.exec(text); match; match = urlPattern.exec(text)) {
        const rawUrl = match[0];
        const start = match.index;
        const end = start + rawUrl.length;

        if (!hasAllowedBoundary(text, start)) {
            continue;
        }

        const {url, suffix} = trimUrlSuffix(rawUrl);
        if (!url || !isSafeHttpUrl(url)) {
            continue;
        }

        appendTextPart(parts, text.slice(cursor, start));
        parts.push({type: 'link', text: url, url});
        appendTextPart(parts, suffix);
        cursor = end;
    }

    appendTextPart(parts, text.slice(cursor));
    return parts;
}
